import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { TranslationHelper } from '@/context/Locale'
import { LANGUAGE_KEY } from '@/context/Locale/Locale'

const CollapsibleErrorToast = ({
	message,
	detail,
}: {
	detail?: string
	message: string
}) => {
	const [expanded, setExpanded] = useState(false)
	const previewLength = 50
	const detailPreview =
		detail && detail.length > previewLength
			? detail.slice(0, previewLength) + '...'
			: detail

	return (
		<div>
			<div className="font-semibold">{message}</div>
			{detail && (
				<div
					className="mt-1 text-sm opacity-80 flex items-center justify-between cursor-pointer"
					onClick={() => setExpanded(!expanded)}
				>
					<span className="flex-1">{expanded ? detail : detailPreview}</span>
					<span className="ml-2">
						<FontAwesomeIcon icon={expanded ? faEyeSlash : faEye} size="sm" />
					</span>
				</div>
			)}
		</div>
	)
}

export interface ErrorContext {
	details?: Record<string, unknown>
	operation: string
}

export const logError = (error: unknown, context: ErrorContext): void => {
	const errorMessage = error instanceof Error ? error.message : String(error)
	const errorStack = error instanceof Error ? error.stack : undefined

	console.error(`[${context.operation}] Error:`, {
		message: errorMessage,
		stack: errorStack,
		...context.details,
		timestamp: new Date().toISOString(),
	})
}

/** User-facing message from RTK Query / API errors. */
export const getApiErrorDescription = (error: unknown): string => {
	if (error && typeof error === 'object' && 'data' in error) {
		const data = (
			error as {
				data?:
					| {
							errorCode?: string
							errorDescription?: string
							errorMessage?: string
							message?: string
					  }
					| string
			}
		).data

		if (typeof data === 'string' && data) return data

		if (data && typeof data === 'object') {
			if (typeof data.errorDescription === 'string' && data.errorDescription) {
				return data.errorDescription
			}
			if (typeof data.errorMessage === 'string' && data.errorMessage) {
				return data.errorMessage
			}
			if (typeof data.message === 'string' && data.message) return data.message
			if (typeof data.errorCode === 'string' && data.errorCode) {
				return data.errorCode
			}
			try {
				const serialized = JSON.stringify(data)
				if (serialized !== '{}') return serialized
			} catch {
				// fall through
			}
		}
	}

	if (error instanceof Error) return error.message

	if (error && typeof error === 'object') {
		try {
			const serialized = JSON.stringify(error)
			if (serialized !== '{}') return serialized
		} catch {
			// fall through
		}
	}

	return String(error)
}

export interface LogErrorAndNotifyParams {
	context: ErrorContext
	detail?: string
	error: unknown
	messageKey?: LANGUAGE_KEY
	t: TranslationHelper
}

export const logErrorAndNotifyWithToast = ({
	error,
	context,
	t,
	messageKey = 'TASK_ERROR',
	detail,
}: LogErrorAndNotifyParams): void => {
	logError(error, context)
	toast(<CollapsibleErrorToast message={t(messageKey)} detail={detail} />, {
		type: 'error',
		autoClose: 20000,
	})
}
